import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Skeleton from '@mui/material/Skeleton';
import React, { memo } from 'react';

import StoryblokPassTierCardsPreset from '../../components/Storyblok/presets/StoryblokPassTierCardsPreset';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {

    console.error('Error caught in ErrorBoundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return null;
    }

    // eslint-disable-next-line react/destructuring-assignment
    return this.props.children;
  }
}

const PassTierSkeleton = () => (
  <Box
    sx={{
      flexGrow: 1,
      p: 0,
    }}
  >
    <Grid container spacing={4}>
      {Array.from({ length: 3 }).map((_, index) => (
        <Grid key={index} size={{ md: 4, sm: 6, xs: 12 }}>
          <Card
            sx={{
              position: 'relative',
              borderColor: 'grey.300',
              borderWidth: 2,
              borderStyle: 'solid',
              boxShadow: 1,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Image */}
            <Skeleton
              height={180}
              sx={{ objectFit: 'contain' }}
              variant="rectangular"
              width="100%"
            />

            {/* Chip */}
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Skeleton height={24} sx={{ mx: 'auto' }} variant="rectangular" width={80} />
            </Box>

            <CardContent sx={{ pt: 2, flexGrow: 1 }}>
              {/* Title */}
              <Skeleton height={28} sx={{ mb: 2, mx: 'auto' }} width="80%" />

              {/* Price and Old Price */}
              <Box alignItems="center" display="flex" justifyContent="center" sx={{ mx: 'auto' }}>
                <Skeleton height={36} width="40%" />
                <Skeleton height={28} sx={{ ml: 2, textDecoration: 'line-through' }} width="30%" />
              </Box>

              {/* SubPriceHeading */}
              <Skeleton height={24} sx={{ mt: 2, mx: 'auto' }} width="50%" />

              {/* Features */}
              <List dense>
                {Array.from({ length: 3 }).map((_, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <Skeleton height={24} variant="circular" width={24} />
                    </ListItemIcon>
                    <Skeleton height={20} width="80%" />
                  </ListItem>
                ))}
              </List>
            </CardContent>

            {/* Buttons */}
            <Box sx={{ px: 2, pb: 2 }}>
              <Skeleton height={48} sx={{ mb: 2, borderRadius: 4 }} width="100%" />
              <Skeleton height={32} sx={{ mx: 'auto', borderRadius: 4 }} width="60%" />
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Box>
);

const SectionPassTiersComponent = ({ products = null, loading = false }) => {
  if (loading && !products) {
    return (
      <Container maxWidth="xl" sx={{ pt: 4, pb: { xs: 4, md: 6 } }}>
        <PassTierSkeleton />
      </Container>
    );
  }

  if (!loading && !products) {
    return null; // Render nothing if no products are available
  }

  return (
    <Container maxWidth="xl" sx={{ pt: 4, pb: { xs: 4, md: 6 } }}>
      <ErrorBoundary>
        <StoryblokPassTierCardsPreset blok={{ dataSource: products }} />
      </ErrorBoundary>
    </Container>
  );
};

SectionPassTiersComponent.displayName = 'SectionPassTiers';

const SectionPassTiers = memo(SectionPassTiersComponent);

export default SectionPassTiers;
